import type { ReactNode } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Chip } from '../index';
import type { Exercise } from '@heraion/shared';

import type { DraftEntry, DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import { makeStyles } from './loggerStyles';
import type { CardHints } from './cardHints';
import type { HoldProps } from './useTransientTip';

// FocusPanelChipRow (timeline logger redesign, 2026-05-31) — the qualifier
// chip row of the FocusPanel. Sits BETWEEN the SetCountRow and the inputs
// (Option B, agreed with Antenore via ASCII mockup): "set how the set counts,
// then type the numbers."
//
// It ports the qualifier chips the card logger's EntryChipRow exposes but the
// minimal Phase-2 FocusPanel never carried — the UNI/BI laterality toggle
// (the actual parity gap) and AMRAP. Reuses the card logger's loggerStyles
// chip look so the two loggers read the same. No new write path: BI/UNI →
// set_entry_loaded_sides, AMRAP → an update_set { amrap } patch (all in the
// parent's callbacks). Renders nothing when no chip applies (e.g. cardio with
// a non-modifiable laterality and no bound set). The RIR/RPE effort-column
// toggle left this row in AP-0b — effort is captured by the meter
// (FocusPanelEffort), always on, so there's no column to show/hide.
//
// #308: a tap always does the chip's job; HOLDING it pins its explanation in
// the card's single balloon (FocusPanel owns it, `hold` wires it). The words
// live in cardHints, not here.
export function FocusPanelChipRow({
  entry,
  set,
  exercise,
  onToggleLoadedSides,
  onToggleAmrap,
  dropsetActive,
  onToggleBurnPlus,
  burnLocked,
  showTip,
  hold,
  hints,
  leading,
  hideBurnPlus = false,
  hideLaterality = false,
  amrapPrescribing = false,
  onToggleTimed,
  timedActive = false,
  onToggleNote,
  noteActive = false,
  theme,
}: {
  entry: DraftEntry;
  /** The bound set, or null when every set is logged. AMRAP is per-set. */
  set: DraftSet | null;
  /** Catalog exercise for the active entry — drives lateralityModifiable. */
  exercise: Exercise | undefined;
  /** set_entry_loaded_sides (1↔2) + bilateral-preference persist (parent). */
  onToggleLoadedSides: (next: 1 | 2) => void;
  /** Toggles the bound set's DraftSet.amrap — the athlete's OUTCOME
   *  ("I went to the limit here") — via an update_set patch. Never
   *  touches amrapPrescribed: the plan's request is not ours to edit,
   *  and a prescribed set the athlete stopped short on must stay
   *  unmarked. */
  onToggleAmrap: () => void;
  /** A Burn+ intensity technique (dropset/myorep) is active on the bound set. */
  dropsetActive: boolean;
  /** Reveal/hide the Dropset|Myo-rep selector bar (state lifted to FocusPanel). */
  onToggleBurnPlus: () => void;
  /** Past-session edit: can't create/change a set-type chain — tap shows a tip. */
  burnLocked: boolean;
  /** The card's balloon, for the laterality explainer and the lock reason. */
  showTip: (message: string, opts?: { pinned?: boolean }) => void;
  /** Hold-for-hint bundle (#308) and the words it shows. */
  hold: HoldProps;
  hints: CardHints;
  /** Leading controls in the SAME wrapping row as the chips: 🕐 history, the
   *  machine tag, the superset, all supplied by the FocusPanel (#308). */
  leading?: ReactNode;
  /** PE-22 K3.1 — prescribe authoring has no dropset/myorep step to run (the
   *  chip's tap is a silent no-op there today), so the mount hides it —
   *  same honesty rule as FocusPanelEffort's `hideFail`. AMRAP + BI/UNI stay
   *  visible: the mockup keeps them, and both are real prescription
   *  commands. */
  hideBurnPlus?: boolean;
  /** PE-35 — prescribe: laterality has no home in a program slot. `SlotDraft`
   *  carries no BI/UNI field, so the chip could only ever toggle a value the
   *  save path throws away; a coach who wants "one arm" picks the one-arm
   *  exercise. Registered as a real gap, not silently dropped. */
  hideLaterality?: boolean;
  /** PE-42 — prescribe: the chip states and writes the coach's REQUEST
   *  (`amrapPrescribed`), not the athlete's outcome (`amrap`), which on a set
   *  nobody has performed is false by construction and made the chip look
   *  permanently off. Same chip, two different facts. */
  amrapPrescribing?: boolean;
  /** PE-22 K3.4 — "A tempo": the reps⇄time toggle, prescribe-editor only.
   *  undefined ⇒ the chip is hidden (same presence-is-visibility convention
   *  `onSplitGroup`/`onCreateGroup` already use on FocusPanelProps), so
   *  'log'/'log-past' — which never pass it — render nothing new here. */
  onToggleTimed?: () => void;
  timedActive?: boolean;
  /** PE-22 K3.4 — "Nota": opens the per-set note editor, prescribe-editor
   *  only. Same undefined-hides convention as onToggleTimed. */
  onToggleNote?: () => void;
  noteActive?: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  // Same visibility rule as EntryChipRow: laterality shows on any weighted
  // lift that is intrinsically unilateral (one-arm/one-leg → seeded ×2) OR
  // can be made unilateral (lateralityModifiable). Before 2026-06-09 the
  // intrinsic ones applied ×2 SILENTLY with no chip, so the user couldn't
  // see or override the count — now the chip is always there to read/flip.
  // Cardio/bodyweight have no per-side load to double.
  const lateralityVisible =
    !hideLaterality &&
    !entry.isCardio &&
    !entry.isTimeBased &&
    (exercise?.lateralityModifiable === true ||
      exercise?.unilateral === true);
  const isUnilateral = entry.loadedSides === 2;

  // AMRAP is a per-set qualifier on strength sets (reps-based). Cardio + timed
  // holds use duration; there's no "as many reps as possible" there. Only
  // meaningful while a set is bound.
  //
  // VISIBLE since measurement-contract slice 1 (next/archive/backlog.md LG-19,
  // [[reference_amrap_via_reps_form]]). It was hidden because the chip toggled
  // a draft-only field: neither the save path nor the LogSet schema carried
  // `amrap`, so a tap died at save, and a control that silently does nothing
  // is worse than no control. The flag now travels end-to-end (DraftSet →
  // workoutPayload → WorkoutSet/LogSet → hydrate) and the API that validates
  // it is deployed. That deploy, not a green test suite, is what earns this
  // `true`.
  //
  // What the chip records is the OUTCOME ("I went to the limit"), never the
  // prescription: `DraftSet.amrapPrescribed` is a separate, display-only field
  // and is excluded from the update_set key union, so this control cannot
  // author a coach's request as if it were the athlete's result.
  const AMRAP_LOGGING_ENABLED = true;
  const amrapVisible =
    AMRAP_LOGGING_ENABLED && set !== null && !entry.isCardio && !entry.isTimeBased;

  // 🔥 Burn+ (dropset/myorep) — same reach as AMRAP: a per-set intensity
  // technique on strength sets. The chip reveals the Dropset|Myo-rep selector
  // inline (replacing the old ⋯ "Set type" menu); selected = a technique is on.
  const burnPlusVisible =
    set !== null && !entry.isCardio && !entry.isTimeBased && !hideBurnPlus;

  // PE-22 K3.4 — "A tempo" / "Nota", prescribe-editor only. Presence of the
  // callback IS the visibility gate (undefined in 'log'/'log-past', which
  // never pass one) — same convention hideBurnPlus/hideFail use, inverted
  // because these two are ADDITIVE to prescribe rather than suppressed from
  // it. "A tempo" shares AMRAP/Burn+'s scope (a reps-based strength set —
  // an exercise already inherently timed has nothing to toggle); "Nota"
  // applies to any bound set.
  const timedVisible =
    onToggleTimed !== undefined && !entry.isCardio && !entry.isTimeBased;
  const noteVisible = onToggleNote !== undefined;

  // The leading controls are always supplied by the FocusPanel, so the row
  // renders even when no qualifier chip applies. Guard kept for any future
  // caller that omits it.
  if (
    !lateralityVisible &&
    !amrapVisible &&
    !burnPlusVisible &&
    !timedVisible &&
    !noteVisible &&
    !leading
  )
    return null;

  return (
    // flexWrap inline (not on the shared entryChipRow style — that's the card
    // logger's EntryChipRow too): 🕐 + ? + the qualifier chips (UNI/BI, AMRAP,
    // RIR, 🔥 Burn+) can exceed a narrow panel and must wrap, not clip (≥44pt).
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.s2 }}>
      {/* The leading controls, then the wrapping qualifier chips. */}
      <View
        style={[
          styles.entryChipRow,
          { flex: 1, flexWrap: 'wrap', gap: theme.spacing.s3 },
        ]}
      >
        {leading}
        {lateralityVisible ? (
        <Chip
          theme={theme}
          dense
          label={isUnilateral ? 'UNI' : 'BI'}
          selected={isUnilateral}
          accessibilityLabel={t(
            isUnilateral ? 'mobile.logger.unilateral' : 'mobile.logger.bilateral',
          )}
          {...hold(hints.laterality(isUnilateral))}
          onPress={() => {
            const next: 1 | 2 = isUnilateral ? 1 : 2;
            onToggleLoadedSides(next);
            // The tap still explains what the new mode counts: the
            // laterality mode is otherwise invisible (2026-06-09).
            showTip(hints.laterality(next === 2));
          }}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {amrapVisible ? (
        <Chip
          theme={theme}
          dense
          label="AMRAP"
          // ⭐ TWO FACTS, one chip, and which one depends on the mode.
          // In LOG it is the OUTCOME, deliberately independent of the
          // prescription badge in FocusPanelInputs: markable on a set no plan
          // asked for, un-markable on a prescribed one.
          // In PRESCRIBE (PE-42) there is no outcome (nobody has performed
          // this set), so it states and writes the coach's REQUEST. Reading
          // `amrap` there would have shown the chip permanently off.
          // ⇒ The accessible name says WHICH of the two, because from the
          // glyph alone a screen-reader user moving between the logger and
          // the editor cannot tell a record from a request.
          selected={
            amrapPrescribing ? set?.amrapPrescribed === true : set?.amrap === true
          }
          accessibilityLabel={
            amrapPrescribing
              ? t('mobile.logger.amrapChipPrescribe', {
                  defaultValue: 'Prescribe as many reps as possible',
                })
              : t('mobile.logger.amrapChip', {
                  defaultValue: 'As many reps as possible',
                })
          }
          {...hold(hints.amrap(amrapPrescribing))}
          onPress={onToggleAmrap}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {burnPlusVisible ? (
        <Chip
          theme={theme}
          dense
          label={t('mobile.logger.burnPlus', { defaultValue: 'Burn+' })}
          selected={dropsetActive}
          accessibilityLabel={t('mobile.logger.burnPlusChip', {
            defaultValue: 'Burn+ intensity technique (dropset / myo-rep)',
          })}
          {...hold(hints.burnPlus)}
          onPress={() => {
            if (burnLocked) {
              showTip(
                t('mobile.timeline.setTypeLocked', {
                  defaultValue:
                    "Editing a past session: you can't change the set type (dropset/myorep).",
                }),
              );
              return;
            }
            onToggleBurnPlus();
          }}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {timedVisible ? (
        <Chip
          theme={theme}
          dense
          label={t('mobile.logger.timedChip', { defaultValue: 'Timed' })}
          selected={timedActive}
          accessibilityLabel={t('mobile.logger.timedChip', { defaultValue: 'Timed' })}
          {...hold(hints.timed)}
          onPress={() => onToggleTimed?.()}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {noteVisible ? (
        <Chip
          theme={theme}
          dense
          label={t('mobile.logger.noteChip', { defaultValue: 'Note' })}
          selected={noteActive}
          accessibilityLabel={t('mobile.logger.noteChip', { defaultValue: 'Note' })}
          {...hold(hints.note)}
          onPress={() => onToggleNote?.()}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      </View>
    </View>
  );
}
